import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { Auth, API } from 'aws-amplify';
import { useAuthenticator } from '@aws-amplify/ui-react';
import * as queries from '../graphql/queries';
import { useOrganization } from '../contexts/OrganizationContext';
import { createOrganizationForUser } from '../utils/createOrganization';
import { hubSignOut } from '../utils/signOut';

/**
 * Dedicated, chrome-free page where a user with NO organization creates one before entering the app.
 * Rendered outside AuthWrapper, so no Navigation/OrganizationSelector (and no stale org) sits behind
 * it. AccountSetupGate is what routes no-org users here.
 */
const AccountSetup = () => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const { hasOrganizations, isLoading } = useOrganization();
  const [form, setForm] = useState({ name: '', contactEmail: '', contactPhone: '' });
  const [invitePending, setInvitePending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Detect a pending invitation (an OrganizationMember row for this email) so we can nudge an invitee
  // toward their invite link instead of only offering "create". Same by-email query the selector uses.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const email = user.attributes.email;
        const res = await API.graphql({
          query: queries.listOrganizationMembers,
          variables: { filter: { email: { eq: email } }, limit: 100 },
        });
        const items = res.data.listOrganizationMembers.items || [];
        if (!cancelled) setInvitePending(items.some((m) => !m._deleted));
      } catch (e) {
        // Non-fatal — the create path still works without invite detection.
        console.warn('Invite lookup failed on account setup:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (authStatus === 'unauthenticated') return <Navigate to="/login" replace />;

  // Don't flash the setup form before the org list is known.
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" role="status" />
      </div>
    );
  }
  // Someone who already has an organization has no business on the setup page — e.g. they navigated
  // here directly. Send them into the app rather than offering to create a duplicate org.
  if (hasOrganizations) return <Navigate to="/dashboard" replace />;

  const onChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const result = await createOrganizationForUser(form);
    if (!result.ok) {
      setError(result.error);
      setSubmitting(false);
      return;
    }
    // Full-page navigation, NOT client-side navigate(): the org was just created but
    // OrganizationContext has not re-fetched, so AccountSetupGate still sees hasOrganizations=false and
    // would bounce a client-side /dashboard route straight back to this form (it looks like "create did
    // nothing"). A full load re-initialises the context with the new org — the same reason
    // OrganizationSelector reloads after creating. Templates, if they failed, are non-fatal and managed
    // later from Organization Management.
    window.location.assign('/dashboard');
  };

  return (
    <Container
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ minHeight: '100vh', maxWidth: 520 }}
    >
      <h2 className="mb-2">Set up your account</h2>
      <p className="text-muted text-center mb-4">
        Create your organization to get started. You'll be its admin.
      </p>

      {invitePending && (
        <Alert variant="info" className="w-100">
          You've been invited to join an organization — open the invite link in your email to accept it.
          Or create your own below.
        </Alert>
      )}

      <Card className="w-100">
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={onSubmit}>
            <Form.Group className="mb-3" controlId="orgName">
              <Form.Label>Organization Name *</Form.Label>
              <Form.Control type="text" value={form.name} onChange={onChange('name')} required />
            </Form.Group>
            <Form.Group className="mb-3" controlId="orgEmail">
              <Form.Label>Contact Email *</Form.Label>
              <Form.Control type="email" value={form.contactEmail} onChange={onChange('contactEmail')} required />
            </Form.Group>
            <Form.Group className="mb-3" controlId="orgPhone">
              <Form.Label>Contact Phone *</Form.Label>
              <Form.Control type="tel" value={form.contactPhone} onChange={onChange('contactPhone')} required />
            </Form.Group>
            <Button type="submit" variant="primary" className="w-100" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Setting up organization…
                </>
              ) : (
                'Create Organization'
              )}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Button variant="link" className="mt-3" onClick={() => hubSignOut()} disabled={submitting}>
        Sign out
      </Button>
    </Container>
  );
};

export default AccountSetup;
